import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

interface PaymentModalProps {
    isOpen: boolean;
    onClose: () => void;
    projectTitle?: string;
}

type PaymentMethod = "momo" | "paypal" | "western_union" | "bank";

const PaymentModal: React.FC<PaymentModalProps> = ({
    isOpen,
    onClose,
    projectTitle,
}) => {
    const { t } = useTranslation();
    const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);

    const paymentMethods = [
        {
            id: "momo",
            name: "Mobile Money",
            icon: (
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 3H7C4.79086 3 3 4.79086 3 7V17C3 19.2091 4.79086 21 7 21H17C19.2091 21 21 19.2091 21 17V7C21 4.79086 19.2091 3 17 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 7V17M8 12H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            ),
            color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
        },
        {
            id: "paypal",
            name: "PayPal",
            icon: (
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 5H5C3.89543 5 3 5.89543 3 7V17C3 18.1046 3.89543 19 5 19H19C20.1046 19 21 18.1046 21 17V7C21 5.89543 20.1046 5 19 5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M7 15L10 9H12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 12H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            ),
            color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        },
        {
            id: "western_union",
            name: "Western Union",
            icon: (
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M2 12H22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            ),
            color: "bg-stone-100 text-stone-700 dark:bg-stone-900/30 dark:text-stone-400",
        },
        {
            id: "bank",
            name: "Bank Deposit",
            icon: (
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 21H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M5 21V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M19 21V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M5 7L12 3L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M10 21V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M14 21V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            ),
            color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        },
    ];

    const renderPaymentDetails = () => {
        switch (selectedMethod) {
            case "momo":
                return (
                    <div className="space-y-4">
                        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-100 dark:border-yellow-800/30">
                            <h4 className="font-bold text-yellow-800 dark:text-yellow-300 mb-2">MTN Mobile Money / Orange Money</h4>
                            <div className="space-y-2 text-sm text-yellow-700 dark:text-yellow-400">
                                <div className="flex justify-between">
                                    <span>Name:</span>
                                    <span className="font-mono font-bold">DDC Official</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>MTN Number:</span>
                                    <span className="font-mono font-bold">+237 6XX XXX XXX</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Orange Number:</span>
                                    <span className="font-mono font-bold">+237 6XX XXX XXX</span>
                                </div>
                            </div>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                            Please use the project name <strong>"{projectTitle}"</strong> as the reference.
                        </p>
                    </div>
                );
            case "paypal":
                return (
                    <div className="space-y-4">
                        <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-100 dark:border-emerald-800/30">
                            <h4 className="font-bold text-emerald-800 dark:text-emerald-300 mb-2">PayPal Transfer</h4>
                            <div className="space-y-2 text-sm text-emerald-700 dark:text-emerald-400">
                                <div className="flex justify-between">
                                    <span>Email:</span>
                                    <span className="font-mono font-bold">donate@ddc.org</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Link:</span>
                                    <a href="#" className="font-bold underline">paypal.me/ddc_community</a>
                                </div>
                            </div>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                            Please include <strong>"{projectTitle}"</strong> in the payment note.
                        </p>
                    </div>
                );
            case "western_union":
                return (
                    <div className="space-y-4">
                        <div className="p-4 bg-stone-50 dark:bg-stone-900/20 rounded-xl border border-stone-100 dark:border-stone-800/30">
                            <h4 className="font-bold text-stone-800 dark:text-stone-300 mb-2">Western Union / MoneyGram</h4>
                            <div className="space-y-2 text-sm text-stone-700 dark:text-stone-400">
                                <div className="flex justify-between">
                                    <span>Receiver Name:</span>
                                    <span className="font-mono font-bold">Djichami Development Council</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Location:</span>
                                    <span className="font-mono font-bold">Yaounde, Cameroon</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Phone:</span>
                                    <span className="font-mono font-bold">+237 6XX XXX XXX</span>
                                </div>
                            </div>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                            After transfer, please send the MTCN code to our contact number.
                        </p>
                    </div>
                );
            case "bank":
                return (
                    <div className="space-y-4">
                        <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-100 dark:border-emerald-800/30">
                            <h4 className="font-bold text-emerald-800 dark:text-emerald-300 mb-2">Bank Transfer</h4>
                            <div className="space-y-2 text-sm text-emerald-700 dark:text-emerald-400">
                                <div className="flex justify-between">
                                    <span>Bank Name:</span>
                                    <span className="font-mono font-bold">Afriland First Bank</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Account Name:</span>
                                    <span className="font-mono font-bold">DDC Association</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Account Number:</span>
                                    <span className="font-mono font-bold">0000 1234 5678 9012</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>SWIFT Code:</span>
                                    <span className="font-mono font-bold">AFRIXXXX</span>
                                </div>
                            </div>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                            Please use <strong>"{projectTitle}"</strong> as the transfer reference.
                        </p>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
                    />

                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                    >
                        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden pointer-events-auto border border-gray-100 dark:border-gray-700 flex flex-col max-h-[90vh]">
                            {/* Header */}
                            <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/50">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                        {t("payment.title", "Make a Contribution")}
                                    </h3>
                                    {projectTitle && (
                                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                            For: <span className="font-semibold text-emerald-600 dark:text-emerald-400">{projectTitle}</span>
                                        </p>
                                    )}
                                </div>
                                <button
                                    onClick={onClose}
                                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-500 dark:text-gray-400"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* Body */}
                            <div className="p-6 overflow-y-auto">
                                {!selectedMethod ? (
                                    <div className="space-y-4">
                                        <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
                                            {t("payment.selectMethod", "Select a payment method to see details:")}
                                        </p>
                                        <div className="grid grid-cols-2 gap-4">
                                            {paymentMethods.map((method) => (
                                                <button
                                                    key={method.id}
                                                    onClick={() => setSelectedMethod(method.id as PaymentMethod)}
                                                    className={`flex flex-col items-center justify-center p-6 rounded-2xl border-2 transition-all duration-200 hover:scale-105 ${method.color} border-transparent hover:border-current bg-opacity-50 hover:bg-opacity-100`}
                                                >
                                                    <div className="mb-3">{method.icon}</div>
                                                    <span className="font-bold text-sm">{method.name}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-6">
                                        <button
                                            onClick={() => setSelectedMethod(null)}
                                            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                            </svg>
                                            {t("payment.back", "Back to methods")}
                                        </button>

                                        <motion.div
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            className="space-y-6"
                                        >
                                            {renderPaymentDetails()}

                                            <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl text-sm text-gray-600 dark:text-gray-300">
                                                <p className="flex gap-2">
                                                    <span className="text-xl">ℹ️</span>
                                                    <span>
                                                        {t("payment.manualNote", "Since automatic payments are not yet available, please make the transfer manually and contact us with your proof of payment for confirmation.")}
                                                    </span>
                                                </p>
                                            </div>
                                        </motion.div>
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="p-6 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
                                <button
                                    onClick={onClose}
                                    className="w-full py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl font-bold hover:opacity-90 transition-opacity"
                                >
                                    {t("common.close", "Close")}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default PaymentModal;
